<template>
    <div class="content mk-lp-edit">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.landing_pages") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6" v-loading="loading">
            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>

            <template v-if="lp">
                <!-- header bar -->
                <card class="mb-3 mk-headbar" body-classes="py-3">
                    <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
                        <div class="flex-fill" style="min-width: 220px;">
                            <h3 class="mb-0 d-inline-block mr-2">{{ form.title || lp.title }}</h3>
                            <el-tag size="small" :type="tagType(lp.live_state)">{{ $t("marketing.lp.state." + (lp.live_state || lp.status)) }}</el-tag>
                            <el-tag v-if="lp.has_unpublished_changes && lp.published_version" size="small" type="warning" class="ml-1">{{ $t("marketing.lp.state.unpublished_changes") }}</el-tag>
                            <el-tag v-if="dirty" size="small" type="danger" effect="plain" class="ml-1" data-testid="lp-dirty">{{ $t("marketing.lp.unsaved") }}</el-tag>
                            <div v-if="lp.published_version" class="small text-muted mt-1">v{{ lp.published_version }} · {{ $t("marketing.lp.published_at") }} {{ fmt(lp.published_at) }}</div>
                        </div>
                        <template v-if="canWrite">
                            <base-button type="secondary" size="sm" :loading="saving" data-testid="lp-save" @click="save()"><i class="fa fa-save"></i> {{ $t("marketing.lp.save_draft") }}</base-button>
                            <base-button type="primary" size="sm" :loading="publishing" data-testid="lp-publish" @click="publish"><i class="fa fa-upload"></i> {{ $t("marketing.lp.publish") }}</base-button>
                        </template>
                        <el-dropdown trigger="click" @command="onMenu">
                            <base-button size="sm" type="neutral" class="px-2"><i class="fa fa-ellipsis-v"></i></base-button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="history">{{ $t("marketing.lp.history") }}</el-dropdown-item>
                                <template v-if="canWrite">
                                    <el-dropdown-item command="unpublish" :disabled="lp.status !== 'published'">{{ $t("marketing.lp.unpublish") }}</el-dropdown-item>
                                    <el-dropdown-item command="rotate">{{ $t("marketing.lp.rotate_preview") }}</el-dropdown-item>
                                </template>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>
                    <el-alert v-if="!canWrite" type="info" :closable="false" class="mt-2" :title="$t('marketing.lp.read_only')"></el-alert>
                    <el-alert v-if="pageErrorList.length" type="error" :closable="false" class="mt-2" show-icon :title="$t('marketing.lp.err.publish_invalid')" data-testid="lp-errors">
                        <ul class="mb-0 pl-3">
                            <li v-for="(e, i) in pageErrorList" :key="i">{{ fieldLabel(e.field) }}: {{ ruleText(e.rule) }}</li>
                        </ul>
                    </el-alert>
                </card>

                <div class="row">
                    <!-- ===== forms (≈ 60 %) ===== -->
                    <div class="col-xl-7 col-lg-7" :class="{ 'mk-ro': !canWrite }">
                        <!-- ข้อมูลหน้า -->
                        <card class="mb-3">
                            <h4 slot="header" class="mb-0">{{ $t("marketing.lp.card_page") }}</h4>
                            <div class="form-group" :class="pcls('title')">
                                <label class="form-control-label">{{ $t("marketing.lp.field.title") }}</label>
                                <el-input v-model="form.title" maxlength="200" show-word-limit :disabled="!canWrite" data-field="title"></el-input>
                                <small v-if="perr('title')" class="text-danger">{{ ruleText(perr("title")) }}</small>
                            </div>
                            <div class="form-group" :class="{ 'mk-invalid': slugState.text && !slugState.ok }">
                                <label class="form-control-label">{{ $t("marketing.lp.field.slug") }}</label>
                                <el-input v-model="form.slug" maxlength="60" :disabled="!canWrite" data-field="slug" @input="onSlugInput">
                                    <template slot="prepend">/lp/</template>
                                </el-input>
                                <small class="d-block mt-1" :class="slugState.ok ? 'text-success' : slugState.text ? 'text-danger' : 'text-muted'">
                                    {{ slugState.text || $t("marketing.lp.slug_hint") }}
                                </small>
                                <small class="d-block text-muted mk-break">{{ pageUrl }}</small>
                            </div>
                            <div class="row">
                                <div class="col-md-6 form-group">
                                    <label class="form-control-label">{{ $t("marketing.lp.field.starts_at") }}</label>
                                    <el-date-picker v-model="form.starts_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" :disabled="!canWrite" clearable></el-date-picker>
                                </div>
                                <div class="col-md-6 form-group" :class="pcls('ends_at')">
                                    <label class="form-control-label">{{ $t("marketing.lp.field.ends_at") }}</label>
                                    <el-date-picker v-model="form.ends_local" type="datetime" value-format="yyyy-MM-ddTHH:mm:ss" format="yyyy-MM-dd HH:mm" class="w-100" :disabled="!canWrite" clearable></el-date-picker>
                                    <small v-if="perr('ends_at')" class="text-danger">{{ ruleText(perr("ends_at")) }}</small>
                                </div>
                                <small class="col-12 text-muted mt--2 mb-2">{{ $t("marketing.lp.hint.window") }}</small>
                            </div>
                            <div class="form-group mb-0" :class="pcls('fallback_url')">
                                <label class="form-control-label">{{ $t("marketing.lp.field.fallback_url") }}</label>
                                <el-input v-model="form.fallback_url" :disabled="!canWrite" :placeholder="$t('marketing.lp.hint.fallback_url')" data-field="fallback_url"></el-input>
                                <small v-if="perr('fallback_url')" class="text-danger">{{ ruleText(perr("fallback_url")) }}</small>
                            </div>
                        </card>

                        <!-- SEO -->
                        <card class="mb-3">
                            <h4 slot="header" class="mb-0">{{ $t("marketing.lp.card_seo") }}</h4>
                            <div class="form-group" :class="pcls('seo.title')">
                                <label class="form-control-label">{{ $t("marketing.lp.field.seo_title") }}</label>
                                <el-input v-model="form.seo.title" maxlength="70" show-word-limit :disabled="!canWrite"></el-input>
                            </div>
                            <div class="form-group" :class="pcls('seo.description')">
                                <label class="form-control-label">{{ $t("marketing.lp.field.seo_description") }}</label>
                                <el-input v-model="form.seo.description" type="textarea" :rows="2" maxlength="160" show-word-limit :disabled="!canWrite"></el-input>
                            </div>
                            <div class="form-group">
                                <label class="form-control-label">{{ $t("marketing.lp.field.og_image") }} <small class="text-muted">{{ $t("marketing.lp.hint.og_image") }}</small></label>
                                <s3-image-upload object-type="storefront" :presign="presign" :disabled="!canWrite" :value="form.seo.og_image_url || ''" @input="$set(form.seo, 'og_image_url', $event)" @update:objectKey="$set(form.seo, 'og_image_key', $event)"></s3-image-upload>
                            </div>
                            <div class="d-flex align-items-center">
                                <el-switch v-model="form.seo.noindex" :disabled="!canWrite"></el-switch>
                                <span class="ml-2">{{ $t("marketing.lp.field.noindex") }}</span>
                            </div>
                        </card>

                        <!-- ลิงก์โฆษณา -->
                        <card class="mb-3">
                            <h4 slot="header" class="mb-0">{{ $t("marketing.lp.ad_link") }}</h4>
                            <el-input :value="adLink" readonly type="textarea" :rows="3" class="mb-2 mk-mono" data-testid="lp-ad-url"></el-input>
                            <div class="d-flex flex-wrap" style="gap: 8px;">
                                <base-button size="sm" type="primary" data-testid="lp-copy-ad" @click="copy(adLink)"><i class="fa fa-copy"></i> {{ $t("marketing.lp.copy_ad_link") }}</base-button>
                                <base-button size="sm" type="secondary" data-testid="lp-copy-params" @click="copy(adParams)"><i class="fa fa-copy"></i> {{ $t("marketing.lp.copy_params") }}</base-button>
                            </div>
                            <small class="d-block text-muted mt-2">{{ adHint }}</small>
                        </card>

                        <!-- sections -->
                        <card class="mb-3">
                            <div slot="header" class="d-flex align-items-center justify-content-between">
                                <h4 class="mb-0">{{ $t("marketing.lp.sections") }} <small class="text-muted">({{ form.sections.length }}/30)</small></h4>
                                <base-button v-if="canWrite" size="sm" type="primary" :disabled="form.sections.length >= 30" data-testid="lp-add-section" @click="typeDialog = true">
                                    <i class="fa fa-plus"></i> {{ $t("marketing.lp.add_section") }}
                                </base-button>
                            </div>
                            <p v-if="!form.sections.length" class="text-muted mb-0">{{ $t("marketing.lp.empty_sections") }}</p>
                            <el-collapse v-model="openPanels">
                                <el-collapse-item v-for="(s, i) in form.sections" :key="s.id" :name="s.id" :class="{ 'mk-sec-invalid': !!sectionErrors[s.id] }" :data-section-id="s.id">
                                    <template slot="title">
                                        <div class="d-flex align-items-center w-100 pr-2 mk-sec-title" @click.stop>
                                            <span class="flex-fill text-truncate" @click="toggle(s.id)">
                                                <i v-if="sectionErrors[s.id]" class="fa fa-exclamation-circle text-danger mr-1"></i>
                                                <strong>{{ $t("marketing.lp.type." + s.type) }}</strong>
                                                <span class="text-muted ml-1">{{ firstText(s) }}</span>
                                            </span>
                                            <el-tooltip :content="$t('marketing.lp.visible')" placement="top">
                                                <el-switch v-model="s.visible" :disabled="!canWrite" class="mx-2"></el-switch>
                                            </el-tooltip>
                                            <template v-if="canWrite">
                                                <base-button size="sm" type="neutral" class="p-1" :disabled="i === 0" :title="$t('marketing.lp.move_up')" @click="moveSection(i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                                                <base-button size="sm" type="neutral" class="p-1" :disabled="i === form.sections.length - 1" :title="$t('marketing.lp.move_down')" @click="moveSection(i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                                                <base-button size="sm" type="danger" class="p-1" :title="$t('marketing.lp.remove_section')" @click="removeSection(i)"><i class="fa fa-trash"></i></base-button>
                                            </template>
                                        </div>
                                    </template>
                                    <lp-section-form
                                        :section="s"
                                        :sections="form.sections"
                                        :errors="sectionErrors[s.id] || {}"
                                        :warn="warnings(s.id)"
                                        :ro="!canWrite"
                                        :label-of="sectionLabel"
                                        @normalize="rebaseline"
                                    ></lp-section-form>
                                </el-collapse-item>
                            </el-collapse>
                        </card>
                    </div>

                    <!-- ===== phone preview (sticky) ===== -->
                    <div class="col-xl-5 col-lg-5">
                        <div class="mk-preview-col">
                            <card body-classes="p-3">
                                <div slot="header" class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 8px;">
                                    <h4 class="mb-0">{{ $t("marketing.lp.preview") }}</h4>
                                    <el-radio-group v-model="previewWidth" size="mini">
                                        <el-radio-button :label="360">360</el-radio-button>
                                        <el-radio-button :label="390">390</el-radio-button>
                                        <el-radio-button :label="1280">{{ $t("marketing.lp.preview_desktop") }}</el-radio-button>
                                    </el-radio-group>
                                </div>
                                <div v-if="previewUrl" class="mk-phone-wrap" :style="wrapStyle">
                                    <iframe
                                        :key="previewKey"
                                        :src="previewUrl"
                                        class="mk-phone"
                                        :style="frameStyle"
                                        title="preview"
                                        data-testid="lp-preview"
                                    ></iframe>
                                </div>
                                <p v-else class="text-muted">{{ $t("marketing.lp.preview_none") }}</p>
                                <a v-if="previewUrl" :href="previewUrl" target="_blank" rel="noopener" class="d-inline-block mt-2 small" data-testid="lp-preview-tab">
                                    <i class="fa fa-external-link-alt"></i> {{ $t("marketing.lp.open_preview_tab") }}
                                </a>
                            </card>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- type picker -->
        <el-dialog :title="$t('marketing.lp.add_section')" :visible.sync="typeDialog" width="720px" append-to-body>
            <div class="row">
                <div v-for="t in types" :key="t" class="col-sm-6 mb-3">
                    <div class="mk-type-card border rounded p-3 h-100" role="button" tabindex="0" :data-type="t" @click="addSection(t)" @keyup.enter="addSection(t)">
                        <strong class="d-block">{{ $t("marketing.lp.type." + t) }}</strong>
                        <small class="text-muted">{{ $t("marketing.lp.type_desc." + t) }}</small>
                    </div>
                </div>
            </div>
        </el-dialog>

        <!-- history -->
        <el-dialog :title="$t('marketing.lp.history')" :visible.sync="historyDialog" width="560px" append-to-body>
            <p v-if="!revisions.length" class="text-muted">{{ $t("marketing.lp.history_empty") }}</p>
            <el-table v-else :data="revisions" size="small">
                <el-table-column :label="$t('marketing.lp.version')" width="90"><template v-slot="{ row }">v{{ row.version }}</template></el-table-column>
                <el-table-column :label="$t('marketing.lp.published_at')"><template v-slot="{ row }">{{ fmt(row.published_at) }}</template></el-table-column>
                <el-table-column :label="$t('marketing.lp.published_by')" prop="published_by_name"></el-table-column>
                <el-table-column v-if="canWrite" width="170" align="right">
                    <template v-slot="{ row }">
                        <base-button size="sm" type="secondary" @click="restore(row.version)">{{ $t("marketing.lp.restore") }}</base-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-dialog>
    </div>
</template>

<script>
import { Tag, Alert, DatePicker, Switch, Collapse, CollapseItem, RadioGroup, RadioButton, Dialog, Table, TableColumn, Dropdown, DropdownMenu, DropdownItem, Loading } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { LandingPageService } from "@/resource";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";
import LpSectionForm from "./components/LpSectionForm.vue";
import { storefrontOrigin } from "@/utils/sellerProductRow";
import { copyToClipboard } from "@/utils/clipboard";
import {
    AD_URL_PARAMS,
    SECTION_TYPES,
    adUrl,
    apiErrorCode,
    bkkLocalToIso,
    defaultSection,
    errorsBySection,
    fmtBkk,
    isSchemaMissing,
    isoToBkkLocal,
    liveStateTag,
    lpUrl,
    newSectionId,
    pageSavePayload,
    previewSrc,
    sanitizeLpHtml,
    slugProblem,
    validateDraft,
    validatePublish,
    warningsFor
} from "@/utils/marketing";

// การตลาด → หน้า Landing → editor (Phase 1 ads spec 2026-10-13 §6.3).
// Left: page / SEO / ad link / sections forms. Right: sticky phone preview
// (www /lp/<slug>?preview=<token>, reloaded after every successful save).
export default {
    name: "MarketingLandingPageEdit",
    directives: { loading: Loading.directive },
    components: {
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [DatePicker.name]: DatePicker,
        [Switch.name]: Switch,
        [Collapse.name]: Collapse,
        [CollapseItem.name]: CollapseItem,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Dialog.name]: Dialog,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        S3ImageUpload, LpSectionForm },
    data() {
        return {
            loading: false,
            saving: false,
            publishing: false,
            schemaMissing: false,
            lp: null,
            form: null,
            snapshot: "",
            errors: [],
            openPanels: [],
            types: SECTION_TYPES,
            typeDialog: false,
            historyDialog: false,
            revisions: [],
            previewWidth: 360,
            previewKey: 0,
            slugState: { ok: true, text: "" },
            slugTimer: null
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" }),
        id() {
            return this.$route.params.id;
        },
        origin() {
            return storefrontOrigin();
        },
        pageUrl() {
            return lpUrl(this.origin, this.form.slug || "…");
        },
        adLink() {
            return adUrl(this.origin, (this.lp && this.lp.slug) || this.form.slug);
        },
        adParams() {
            return AD_URL_PARAMS;
        },
        // Raw message: the {{…}} in the copy must not go through vue-i18n's
        // {placeholder} formatting.
        adHint() {
            const m = this.$i18n.messages[this.$i18n.locale] || {};
            return (((m.marketing || {}).lp || {}).ad_link_hint) || "";
        },
        previewUrl() {
            if (!this.lp || !this.lp.preview_token) return "";
            return previewSrc(this.origin, this.lp.slug, this.lp.preview_token, this.lp.updated_at);
        },
        frameStyle() {
            const desktop = this.previewWidth === 1280;
            return {
                width: this.previewWidth + "px",
                height: (desktop ? 1600 : 720) + "px",
                transform: desktop ? "scale(0.4)" : "none",
                transformOrigin: "0 0"
            };
        },
        wrapStyle() {
            const desktop = this.previewWidth === 1280;
            return { width: (desktop ? 512 : this.previewWidth) + "px", height: (desktop ? 640 : 720) + "px" };
        },
        payload() {
            if (!this.form) return null;
            return pageSavePayload(this.pageForValidation, "");
        },
        pageForValidation() {
            return {
                ...this.form,
                starts_at: bkkLocalToIso(this.form.starts_local),
                ends_at: bkkLocalToIso(this.form.ends_local)
            };
        },
        dirty() {
            return !!this.form && JSON.stringify(this.payload) !== this.snapshot;
        },
        grouped() {
            return errorsBySection(this.errors);
        },
        sectionErrors() {
            const g = { ...this.grouped };
            delete g[""];
            return g;
        },
        pageErrors() {
            return this.grouped[""] || {};
        },
        pageErrorList() {
            return this.errors.filter((e) => !e.section_id);
        }
    },
    watch: {
        id: { immediate: true, handler: "load" }
    },
    beforeRouteLeave(to, from, next) {
        if (!this.dirty) return next();
        swal.fire({
            text: this.$t("marketing.lp.confirm_leave"),
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: this.$t("marketing.lp.confirm"),
            cancelButtonText: this.$t("marketing.lp.cancel"),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d"
        }).then((r) => next(!!r.value));
    },
    methods: {
        fmt: fmtBkk,
        tagType: liveStateTag,
        presign(req) {
            return LandingPageService.presign(req);
        },
        perr(f) {
            return this.pageErrors[f];
        },
        pcls(f) {
            return { "mk-invalid": !!this.pageErrors[f] };
        },
        ruleText(r) {
            const k = "marketing.lp.rule." + r;
            return this.$te(k) ? this.$t(k) : r;
        },
        fieldLabel(f) {
            const map = { title: "title", ends_at: "ends_at", fallback_url: "fallback_url", "seo.title": "seo_title", "seo.description": "seo_description" };
            if (map[f]) return this.$t("marketing.lp.field." + map[f]);
            if (f === "sections") return this.$t("marketing.lp.sections");
            return f;
        },
        // Apply an editor-normalisation change without marking the page dirty
        // (only when it was clean; a page with real edits stays dirty).
        rebaseline(apply) {
            const clean = !this.dirty;
            apply();
            if (clean) this.snapshot = JSON.stringify(this.payload);
        },
        warnings(sid) {
            return warningsFor(this.lp && this.lp.warnings, sid);
        },
        sectionLabel(s) {
            return `${this.$t("marketing.lp.type." + s.type)} — ${this.firstText(s) || s.id}`;
        },
        firstText(s) {
            return s.title || s.label || s.title_override || s.code || (s.type === "rich_text" ? String(s.html || "").replace(/<[^>]+>/g, " ").trim().slice(0, 40) : "");
        },
        toggle(id) {
            const i = this.openPanels.indexOf(id);
            if (i === -1) this.openPanels.push(id);
            else this.openPanels.splice(i, 1);
        },
        // ---- load / form ----
        apply(lp) {
            this.lp = lp;
            this.form = {
                title: lp.title || "",
                slug: lp.slug || "",
                starts_local: isoToBkkLocal(lp.starts_at),
                ends_local: isoToBkkLocal(lp.ends_at),
                fallback_url: lp.fallback_url || "/",
                seo: { title: "", description: "", og_image_key: "", og_image_url: "", noindex: true, ...(lp.seo || {}) },
                // rich_text is sanitised on the way in, before any editor sees it.
                sections: JSON.parse(JSON.stringify(lp.sections || []))
                    .map((s) => (s.type === "rich_text" ? { ...s, html: sanitizeLpHtml(s.html) } : s))
                    .map((s) => ({ ...defaultSection(s.type, s.id), ...s }))
            };
            this.snapshot = JSON.stringify(this.payload);
            this.slugState = { ok: true, text: "" };
        },
        async load() {
            if (!this.id) return;
            this.loading = true;
            this.errors = [];
            try {
                const res = await LandingPageService.get(this.id);
                this.apply((res.data && res.data.data) || {});
                this.schemaMissing = false;
            } catch (err) {
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.$notify({ type: "danger", message: this.$t("marketing.lp.err.load") });
            } finally {
                this.loading = false;
            }
        },
        onSlugInput() {
            const slug = (this.form.slug || "").trim();
            if (slug === this.lp.slug) {
                this.slugState = { ok: true, text: "" };
                return;
            }
            const local = slugProblem(slug);
            if (local) {
                this.slugState = { ok: false, text: this.$t("marketing.lp.slug_status." + local) };
                return;
            }
            clearTimeout(this.slugTimer);
            this.slugTimer = setTimeout(async () => {
                try {
                    const res = await LandingPageService.checkSlug(slug, this.lp.id);
                    const d = (res.data && res.data.data) || {};
                    this.slugState = d.available
                        ? { ok: true, text: this.$t("marketing.lp.slug_status.available") }
                        : { ok: false, text: this.$t("marketing.lp.slug_status." + (d.reason || "taken")) };
                } catch (e) {
                    this.slugState = { ok: true, text: "" };
                }
            }, 300);
        },
        // ---- sections ----
        addSection(type) {
            const id = newSectionId(this.form.sections.map((s) => s.id));
            const s = defaultSection(type, id);
            // A hero goes first (publish rule); everything else at the end.
            if (type === "hero") this.form.sections.unshift(s);
            else this.form.sections.push(s);
            this.openPanels.push(id);
            this.typeDialog = false;
        },
        moveSection(i, dir) {
            const arr = this.form.sections;
            const j = i + dir;
            if (j < 0 || j >= arr.length) return;
            const tmp = arr[i];
            this.$set(arr, i, arr[j]);
            this.$set(arr, j, tmp);
        },
        async removeSection(i) {
            const r = await swal.fire({
                text: this.$t("marketing.lp.confirm_remove_section"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonText: this.$t("marketing.lp.confirm"),
                cancelButtonText: this.$t("marketing.lp.cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            });
            if (r.value) this.form.sections.splice(i, 1);
        },
        // ---- save / publish ----
        showErrors(errs) {
            this.errors = errs;
            const ids = Object.keys(errorsBySection(errs)).filter((k) => k);
            ids.forEach((id) => {
                if (this.openPanels.indexOf(id) === -1) this.openPanels.push(id);
            });
        },
        async handleError(err) {
            const code = apiErrorCode(err);
            if (code === "publish_invalid") {
                const errs = (err.response.data && err.response.data.errors) || [];
                this.showErrors(errs);
                this.$notify({ type: "danger", message: this.$t("marketing.lp.err.publish_invalid") });
                return;
            }
            if (code === "stale_draft") {
                const r = await swal.fire({
                    text: this.$t("marketing.lp.err.stale_draft"),
                    icon: "warning",
                    showCancelButton: true,
                    reverseButtons: true,
                    confirmButtonText: this.$t("marketing.lp.reload"),
                    cancelButtonText: this.$t("marketing.lp.cancel")
                });
                if (r.value) {
                    this.snapshot = JSON.stringify(this.payload);
                    this.load();
                }
                return;
            }
            if (code === "slug_taken" || code === "invalid_slug") {
                this.slugState = { ok: false, text: this.$t("marketing.lp.err." + code) };
            }
            if (isSchemaMissing(err)) this.schemaMissing = true;
            const key = "marketing.lp.err." + code;
            this.$notify({ type: "danger", message: code && this.$te(key) ? this.$t(key) : this.$t("marketing.lp.err.generic") });
        },
        async save(silent) {
            const errs = validateDraft(this.pageForValidation);
            if (errs.length || !this.slugState.ok) {
                this.showErrors(errs);
                this.$notify({ type: "danger", message: this.$t("marketing.lp.err.publish_invalid") });
                return false;
            }
            this.saving = true;
            try {
                const res = await LandingPageService.update(this.lp.id, pageSavePayload(this.pageForValidation, this.lp.updated_at));
                this.apply((res.data && res.data.data) || this.lp);
                this.errors = [];
                this.previewKey += 1;
                if (!silent) this.$notify({ type: "success", message: this.$t("marketing.lp.saved") });
                return true;
            } catch (err) {
                await this.handleError(err);
                return false;
            } finally {
                this.saving = false;
            }
        },
        async publish() {
            const errs = validatePublish(this.pageForValidation, Date.now());
            if (errs.length) {
                this.showErrors(errs);
                this.$notify({ type: "danger", message: this.$t("marketing.lp.err.publish_invalid") });
                return;
            }
            if (this.dirty && !(await this.save(true))) return;
            this.publishing = true;
            try {
                const res = await LandingPageService.publish(this.lp.id, this.lp.updated_at);
                this.apply((res.data && res.data.data) || this.lp);
                this.errors = [];
                this.previewKey += 1;
                this.$notify({ type: "success", message: this.$t("marketing.lp.published") });
            } catch (err) {
                await this.handleError(err);
            } finally {
                this.publishing = false;
            }
        },
        async copy(text) {
            const ok = await copyToClipboard(text);
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.lp.copied") : this.$t("marketing.lp.err.generic") });
        },
        async confirm(key) {
            const r = await swal.fire({
                text: this.$t(key),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonText: this.$t("marketing.lp.confirm"),
                cancelButtonText: this.$t("marketing.lp.cancel"),
                cancelButtonColor: "#6c757d"
            });
            return !!r.value;
        },
        async onMenu(cmd) {
            try {
                if (cmd === "history") {
                    const res = await LandingPageService.revisions(this.lp.id);
                    this.revisions = (res.data && res.data.data) || [];
                    this.historyDialog = true;
                } else if (cmd === "unpublish") {
                    if (!(await this.confirm("marketing.lp.confirm_unpublish"))) return;
                    const res = await LandingPageService.unpublish(this.lp.id);
                    this.apply((res.data && res.data.data) || this.lp);
                    this.$notify({ type: "success", message: this.$t("marketing.lp.unpublished") });
                } else if (cmd === "rotate") {
                    if (!(await this.confirm("marketing.lp.confirm_rotate"))) return;
                    const res = await LandingPageService.rotatePreviewToken(this.lp.id);
                    const tok = res.data && res.data.data && res.data.data.preview_token;
                    if (tok) this.$set(this.lp, "preview_token", tok);
                    this.previewKey += 1;
                    this.$notify({ type: "success", message: this.$t("marketing.lp.rotated") });
                }
            } catch (err) {
                await this.handleError(err);
            }
        },
        async restore(version) {
            if (this.dirty && !(await this.confirm("marketing.lp.confirm_leave"))) return;
            if (!(await this.confirm("marketing.lp.confirm_restore"))) return;
            try {
                const res = await LandingPageService.restore(this.lp.id, version);
                this.apply((res.data && res.data.data) || this.lp);
                this.historyDialog = false;
                this.previewKey += 1;
                this.$notify({ type: "success", message: this.$t("marketing.lp.restored") });
            } catch (err) {
                await this.handleError(err);
            }
        }
    }
};
</script>

<style scoped>
.mk-preview-col {
    position: sticky;
    top: 90px;
}
.mk-phone-wrap {
    max-width: 100%;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 18px;
    background: #fff;
}
.mk-phone {
    border: 0;
    display: block;
}
.mk-break {
    word-break: break-all;
}
.mk-type-card {
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
}
.mk-type-card:hover,
.mk-type-card:focus {
    border-color: #5e72e4 !important;
    box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.15);
    outline: none;
}
.mk-sec-title {
    line-height: 1.3;
}
.mk-lp-edit >>> .mk-invalid .el-input__inner,
.mk-lp-edit >>> .mk-invalid .el-textarea__inner,
.mk-lp-edit >>> .mk-invalid .s3-image-upload__drop {
    border-color: #f5365c !important;
}
.mk-lp-edit >>> .mk-sec-invalid .el-collapse-item__header {
    border-left: 3px solid #f5365c;
    padding-left: 6px;
}
.mk-mono >>> textarea {
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
}
</style>
